import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './tv-guide.module.scss';

import {
  FocusContext,
  doesFocusableExist,
  getCurrentFocusKey,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { Clock } from '@smart-tv/components/clock/clock';
import DragScroll from '@smart-tv/components/drag-scroll/drag-scroll';
import Fade from '@smart-tv/components/fade/fade';
import { HEADER_FOCUS_KEYS, HEADER_TVGUIDE } from '@smart-tv/components/header/header-helpers';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';
import useDialog from '@smart-tv/hooks/use-dialog';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import useRepeater from '@smart-tv/hooks/use-repeater';
import {
  MINUTES_OF_A_FULL_DAY,
  NOW_FOCUS_KEY,
  getCurrentTimeScrollPosition
} from '@smart-tv/pages/tv-guide/utils/tv-guide-helpers';
import { TvGuideChannel } from '@smart-tv/pages/tv-guide/types';
import getElapsedMinutesOfTheDay from '@smart-tv/utils/date/get-elapsed-minutes-of-day';
import getTimeFormatted from '@smart-tv/utils/date/get-time-formatted';
import isToday from '@smart-tv/utils/date/is-today';
import removeQueryParams from '@smart-tv/utils/routing/remove-query-params';

import TvGuideContext from '@smart-tv/pages/tv-guide/utils/tv-guide-context';
import { TvGuideSkeletonGrid } from './tv-guide-skeleton';
import TvGuideChannels from './tv-guide-channels';
import TvGuideDayViewContext from '../utils/tv-guide-day-view-context';
import TvGuideImage from './tv-guide-image';
import TvGuideLiveMarker from './tv-guide-live-marker';
import TvGuideMoreInfo from './tv-guide-more-info';
import TvGuidePrograms from './tv-guide-programs';
import TvGuideTimeline from './tv-guide-timeline';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import FavoritesEditContext from '@smart-tv/contexts/favorites-edit-context';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import { ArrowDownIcon } from '@smart-tv/components/icons/arrow-down';
import { isBackKey } from '@smart-tv/utils/remoteKeys';

interface Props {
  channels: TvGuideChannel[];
  getChannels: () => void;
}

export const TvGuideView: React.FC<Props> = (props: Props) => {
  const { channels, getChannels } = props;
  const location = useLocation();

  const { selectedDate, isChannelsLoading } = React.useContext(TvGuideContext);
  const {
    scroll,
    setScroll,
    time,
    setTime,
    setActiveCenter,
    activeProgram,
    activeChannelIndex,
    scrollXRef,
    scrollYRef,
    activeTVGuideFocusKey,
    setActiveTVGuideFocusKey,
    gridFocused,
    setVerticalArrowPress,
    resetFocusAnchors,
    scale,
    scaledConstants
  } = React.useContext(TvGuideDayViewContext);
  const {
    setPendingScrollToLive,
    setScrollToCurrentTimeAction,
    savedTimelinePosition,
    setSavedTimelinePosition
  } = React.useContext(TvGuideContext);

  const { ref, focusKey } = useFocusable();
  const { player, onSetPlayerFocusKey } = usePlayerContext();
  const { opened: dialogOpen, handleSetDialogFocusKey } = useDialog();
  const { open: favoritesOpen } = React.useContext(FavoritesEditContext);

  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.focusFirst !== true || !activeProgram?.eventId) return;

    const targetFocusKey = `${activeChannelIndex}-${activeProgram.eventId}`;
    if (!doesFocusableExist(targetFocusKey)) return;

    setActiveCenter(
      ((activeProgram.startTimeInMinutes + activeProgram.endTimeInMinutes) / 2) *
        scaledConstants.minuteWidth
    );
    setVerticalArrowPress(true);
    setFocus(targetFocusKey);
    navigate(location.pathname, { replace: true, state: null });
  }, [
    activeChannelIndex,
    activeProgram?.eventId,
    activeProgram?.endTimeInMinutes,
    activeProgram?.startTimeInMinutes,
    location.pathname,
    location.state,
    navigate,
    scroll.bottom,
    scroll.left,
    scroll.right,
    scroll.top,
    setActiveCenter,
    scaledConstants.minuteWidth,
    setVerticalArrowPress
  ]);

  const minuteRepeater = useRepeater(60000, true);

  const { checkAndUpdateNetwork, setIsOffline, setIsNetworkDialogSuppressed } = useNetwork();

  const scrollToCurrentTime = useCallback(async () => {
    // This is the "Lige nu" action path.
    // It must always scroll to the current-time coordinate using the SAME minute-width
    // currently used by rendered program blocks, otherwise focus and marker alignment break.
    setIsNetworkDialogSuppressed(false);
    setIsOffline(false);
    setSavedTimelinePosition(null);
    scrollLeftRef.current = 0;
    hasInitializedScroll.current = false; // Reset so loading effect scrolls to current time
    const hasNetwork = await checkAndUpdateNetwork();
    if (hasNetwork) {
      setPendingScrollToLive(false);
      navigate(removeQueryParams('date'));
      // Only scroll if content is visible (not loading)
      // If loading, the effect will scroll when loading finishes
      if (!isChannelsLoading) {
        scrollXRef.current?.scrollTo({
          left: getCurrentTimeScrollPosition(
            getElapsedMinutesOfTheDay(),
            scaledConstants.minuteWidth
          )
        });
      }
      resetFocusAnchors();
    } else {
      setPendingScrollToLive(true);
    }
  }, [
    navigate,
    scrollXRef,
    checkAndUpdateNetwork,
    setPendingScrollToLive,
    setIsOffline,
    setIsNetworkDialogSuppressed,
    setSavedTimelinePosition,
    isChannelsLoading,
    resetFocusAnchors,
    scaledConstants.minuteWidth
  ]);

  // Saving scrollToCurrentTime in context so TvGuide can call it as the tryagain action method is in tvguide
  useEffect(() => {
    setScrollToCurrentTimeAction(() => scrollToCurrentTime);
  }, [scrollToCurrentTime, setScrollToCurrentTimeAction]);

  const setHorizontalScroll = useCallback(
    ({ left, right }: { top: number; right: number; bottom: number; left: number }) => {
      setScroll((prev) => ({ ...prev, left, right }));
    },
    [setScroll]
  );
  const setVerticalScroll = useCallback(
    ({ top, bottom }: { top: number; right: number; bottom: number; left: number }) => {
      setScroll((prev) => ({ ...prev, top, bottom }));
    },
    [setScroll]
  );

  const scrollLeftRef = React.useRef<number>(0);
  const hasInitializedScroll = React.useRef(false);
  const lastScrollElementRef = React.useRef<HTMLDivElement | null>(null);
  const activeChannelIndexRef = React.useRef<number>(activeChannelIndex);

  useEffect(() => {
    activeChannelIndexRef.current = activeChannelIndex;
  }, [activeChannelIndex]);

  useEffect(() => {
    scrollLeftRef.current = scroll.left;
  }, [scroll.left]);

  // Save timeline position when loading starts (before skeleton shows)
  // Restore when loading ends (after scroll content remounts)
  useLayoutEffect(() => {
    if (scrollXRef.current !== lastScrollElementRef.current) {
      lastScrollElementRef.current = scrollXRef.current;
      hasInitializedScroll.current = false;
    }

    if (isChannelsLoading) {
      // Save position before skeleton replaces scroll content
      if (scrollLeftRef.current > 0) {
        setSavedTimelinePosition(scrollLeftRef.current);
      }
    } else {
      // Loading finished, restore position or scroll to current time
      if (savedTimelinePosition !== null) {
        // User had an explicit position before reload; restore exactly.
        scrollXRef.current?.scrollTo({ left: savedTimelinePosition });
        setSavedTimelinePosition(null);
      } else if (!hasInitializedScroll.current) {
        // First-time entry: default to "now" in scaled coordinates.
        scrollXRef.current?.scrollTo({
          left: getCurrentTimeScrollPosition(
            getElapsedMinutesOfTheDay(),
            scaledConstants.minuteWidth
          )
        });
      }
      hasInitializedScroll.current = true;
    }
  }, [
    isChannelsLoading,
    savedTimelinePosition,
    setSavedTimelinePosition,
    scrollXRef,
    scaledConstants.minuteWidth
  ]);

  // After channels reload, scroll Y back to the active channel row
  useLayoutEffect(() => {
    if (!isChannelsLoading && activeChannelIndexRef.current > 0) {
      scrollYRef.current?.scrollTo({
        top:
          activeChannelIndexRef.current *
            (scaledConstants.programHeight + scaledConstants.programGap) -
          scaledConstants.programHeight * 2 -
          scaledConstants.programGap * 2
      });
    }
  }, [isChannelsLoading, scrollYRef, scaledConstants.programHeight, scaledConstants.programGap]);

  // Cleanup: save timeline position when leaving TvGuide
  useEffect(() => {
    return () => {
      if (scrollLeftRef.current > 0) {
        setSavedTimelinePosition(scrollLeftRef.current);
      }
    };
  }, [setSavedTimelinePosition]);

  useEffect(() => {
    setTime({
      minutes: getElapsedMinutesOfTheDay(),
      formatted: getTimeFormatted(),
      unix: new Date().valueOf() / 1000
    });

    // if the day changes while watching the day guide, reload to reinitialize everything on the new day
    if (selectedDate.isToday && !isToday(selectedDate.date)) {
      getChannels();
      scrollToCurrentTime();
      if (player) {
        return onSetPlayerFocusKey(NOW_FOCUS_KEY);
      }
      if (dialogOpen) {
        handleSetDialogFocusKey(NOW_FOCUS_KEY);
      }
    }
  }, [
    minuteRepeater,
    setTime,
    selectedDate,
    getChannels,
    scrollToCurrentTime,
    dialogOpen,
    handleSetDialogFocusKey,
    onSetPlayerFocusKey,
    player
  ]);

  const onButtonRowArrowPress = useCallback(
    (direction: string) => {
      if (direction === 'down') {
        // Block down navigation while channels are loading
        if (isChannelsLoading) {
          return true;
        }
        setVerticalArrowPress(true);
        if (activeTVGuideFocusKey) {
          setFocus(activeTVGuideFocusKey);
          setActiveTVGuideFocusKey(undefined);
          return false;
        } else {
          // No saved focus target: choose center point based on current viewport and scale.
          setActiveCenter(
            scroll.left + (scroll.right - scroll.left) / 2 - scaledConstants.timeSpanWidth
          );
          return false;
        }
      }
      return true;
    },
    [
      isChannelsLoading,
      activeTVGuideFocusKey,
      setActiveTVGuideFocusKey,
      setVerticalArrowPress,
      setActiveCenter,
      scroll.left,
      scroll.right,
      scaledConstants.timeSpanWidth
    ]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (
        HEADER_FOCUS_KEYS.includes(getCurrentFocusKey()) ||
        !isBackKey(event) ||
        dialogOpen ||
        favoritesOpen ||
        player
      )
        return;

      const currentFocusKey = getCurrentFocusKey();

      if (currentFocusKey === NOW_FOCUS_KEY) {
        setFocus(HEADER_TVGUIDE);
        return;
      }

      if (gridFocused) {
        setActiveTVGuideFocusKey(currentFocusKey);
      }

      setFocus(NOW_FOCUS_KEY);
    },
    [dialogOpen, favoritesOpen, player, gridFocused, setActiveTVGuideFocusKey]
  );
  const handleScroll = React.useCallback(
    (left: boolean) => {
      const el = scrollXRef?.current;
      if (!el) return;
      const amount = (el.clientWidth * 4) / 5;
      el.scrollBy({ left: left ? amount : -amount, behavior: 'smooth' });
    },
    [scrollXRef]
  );

  const leftScroll = React.useCallback(() => {
    handleScroll(false);
  }, [handleScroll]);

  const rightScroll = React.useCallback(() => {
    handleScroll(true);
  }, [handleScroll]);

  useEventListener('keydown', (event) => handleKeyPress(event as KeyboardEvent));

  return (
    <FocusContext.Provider value={focusKey}>
      <Fade in={channels.length > 0} unmountOnExit>
        <div
          className={styles['tv-guide']}
          ref={ref}
          style={{ '--tv-scale': scale } as React.CSSProperties}
        >
          <div>
            <TvGuideMoreInfo
              onLiveClick={() => scrollToCurrentTime()}
              onArrowPress={onButtonRowArrowPress}
            />
            <TvGuideImage />

            {!IS_BROWSER && <Clock className={styles['tv-guide__clock']} />}

            <TvGuideTimeline />
          </div>
          {isChannelsLoading ? (
            <TvGuideSkeletonGrid />
          ) : (
            <div className={styles['tv-guide__grid-area']}>
              {IS_BROWSER && (
                <div className={styles['tv-guide__button-container']}>
                  <IconButton
                    size="xl"
                    aria-label="scroll til højre"
                    background={true}
                    onClick={rightScroll}
                    icon={<ArrowDownIcon style={{ transform: 'rotate(-90deg)' }} />}
                  >
                    Frem
                  </IconButton>
                  <IconButton
                    size="xl"
                    aria-label="scroll til venstre"
                    onClick={leftScroll}
                    background={true}
                    className={styles['tv-guide__arrow-button-bottom']}
                    icon={<ArrowDownIcon style={{ transform: 'rotate(90deg)' }} />}
                  >
                    Tilbage
                  </IconButton>
                </div>
              )}
              <DragScroll
                className={styles['tv-guide__scrollable-y']}
                innerRef={scrollYRef}
                direction="vertical"
                disableDrag
                hideScrollbar
                onScroll={setVerticalScroll}
              >
                {selectedDate.isToday && (
                  <TvGuideLiveMarker
                    offsetX={time.minutes * scaledConstants.minuteWidth - scroll.left}
                    height={
                      channels.length *
                        (scaledConstants.programHeight + scaledConstants.programGap) -
                      scaledConstants.programGap
                    }
                  />
                )}
                <TvGuideChannels channels={channels} />
                <DragScroll
                  className={styles['tv-guide__scrollable-x']}
                  innerRef={scrollXRef}
                  direction="horizontal"
                  hideScrollbar
                  onScroll={setHorizontalScroll}
                >
                  <div
                    className={styles['tv-guide__grid']}
                    style={
                      {
                        '--grid-height': `${
                          channels.length *
                          (scaledConstants.programHeight + scaledConstants.programGap)
                        }px`,
                        '--grid-width': `${
                          MINUTES_OF_A_FULL_DAY * scaledConstants.minuteWidth +
                          scaledConstants.gridSpacing
                        }px`
                      } as React.CSSProperties
                    }
                  >
                    <TvGuidePrograms channels={channels} />
                  </div>
                </DragScroll>
              </DragScroll>
            </div>
          )}
        </div>
      </Fade>
    </FocusContext.Provider>
  );
};

export default React.memo(TvGuideView);
